import React, { useState } from "react";
import { endpoints } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";

/**
 * User-facing "Redeem Pro Code" panel.
 *
 * Shows an active complimentary access summary when the caller already
 * has one. Otherwise renders the redemption form. Never trusts frontend
 * state — the backend is the source of truth; after redemption we
 * refresh the SubscriptionContext to reflect the new tier.
 */
function _formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function RedeemProCodePanel() {
  const { refresh, sources, tier } = useSubscription();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const comp = (sources || []).find((s) => s.kind === "complimentary");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await endpoints.redeemProCode(code.trim());
      await refresh();
      setCode("");
      toast.success("Your Pro access is active.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please check your code and try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="redeem-pro-code"
      className="p-6 md:p-7 border hairline rounded-md bg-[hsl(var(--paper))]"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
        <Sparkles size={12} />
        <span>Redeem Pro Code</span>
      </div>

      {comp ? (
        <div className="mt-3" data-testid="complimentary-active">
          <div className="leading-tight"><BrandMark className="text-2xl" signOnMount={false} testid="redeem-complimentary-brand" /> <span className="font-display text-2xl">Pro</span></div>
          <div className="text-sm text-[hsl(var(--fg))]/85">
            Complimentary Access
            {comp.description ? <span className="text-[hsl(var(--muted-foreground))]"> · {comp.description}</span> : null}
          </div>
          {comp.expires_at && (
            <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1" data-testid="complimentary-expires">
              Active until {_formatDate(comp.expires_at)}.
            </div>
          )}
          <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] max-w-lg">
            All Pro features are unlocked for you. Your journey, journal, rituals, and reflections stay yours forever — even after your complimentary access ends.
          </p>
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
            {tier === "pro"
              ? "You're already on Pro. If you also have a complimentary code, redeeming it stacks — you keep Pro even after the paid subscription ends."
              : "Received a Pro code from us? Enter it below to unlock everything Pro offers."}
          </p>
          <form onSubmit={submit} className="mt-4 flex items-center gap-2 max-w-md">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. MXVR-VQV7-P889"
              data-testid="redeem-code-input"
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              className="tracking-widest uppercase"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            />
            <Button type="submit" disabled={busy || !code.trim()} data-testid="redeem-code-submit">
              {busy ? "Checking…" : "Redeem"}
            </Button>
          </form>
        </>
      )}
    </section>
  );
}
